import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";

import { Box, Drawer, Grid, Button, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { LoadingScreen } from '../../../partials/LoadingScreen';

import apiClient from '../../../../services/api';

import store from '../../../../store';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { setProjectIsLoading, setSelectedProject, setSideBarIsOpened } from '../../../../store/actions/Project';

import BackArrowIcon from '../../../icons/BackArrowIcon';
import BackIcon from '../../../icons/BackIcon';

import Main from '../../../styled/Main';
import DrawerHeader from '../../../styled/DrawerHeader';

import ProjectSidePanel from './ProjectSidePanel';

import { Refresh } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { setBackgrounds } from '../../../../store/actions/Backgrounds';
import { getSelectedObject } from '../helpers';

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;

    return { width, height };
}

function ProjectContainer() {
    const { id } = useParams();
    const { t } = useTranslation();

    const navigate = useNavigate();
    const theme = useTheme();

    const project = useSelector((state) => state.project.selectedProject);
    const open = useSelector((state) => state.project.sideBarIsOpened);
    const selectedMeshId = useSelector((state) => state.project.selectedMeshId);
    const projectElements = useSelector((state) => state.project.elements);

    const [loading, setLoading] = useState(true);
    const [drawerWidth, setDrawerWidth] = useState(500);
    const [isAREnabled, setIsAREnabled] = useState(false);

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        setLoading(true);

        apiClient.post('/api/modules/check', {name: 'easyBoardOnline'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })

        apiClient.post('/api/modules/check', {name: 'AR'}).then(response => {
            if(!response.data.success) {
                setIsAREnabled(false);
            } else {
                setIsAREnabled(true);
            }
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        store.dispatch(setSelectedProject(null));
        store.dispatch(setProjectIsLoading(true));

        apiClient.post('/api/projects/load', { id: Number(id) }).then((response) => {
            if (!response.data.success) {
                return;
            }

            if (response?.data?.backgrounds) {
                const backgroundsData = response.data.backgrounds;
                delete response.data.backgrounds;
                store.dispatch(setBackgrounds(backgroundsData));
            }

            store.dispatch(setSelectedProject(response.data));
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t('An error occurred while selecting project'), "error"))
        }).finally(() => {
            store.dispatch(setProjectIsLoading(false));
        });
    }, [id]);

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [])

    useEffect(() => {
        if(windowDimensions) {
            store.dispatch(setSideBarIsOpened(windowDimensions.width > 1200 ? true : false));
        }
    }, []);

    useEffect(() => {
        if(windowDimensions.width < 650) {
            setDrawerWidth(windowDimensions.width - 89);
        }
    }, [windowDimensions]);

    useEffect(() => {
        if (selectedMeshId) {
            const selectedObject = getSelectedObject(projectElements, selectedMeshId);

            if (selectedObject?.is_product) {
                store.dispatch(setSideBarIsOpened(true));
            }
        }
    }, [selectedMeshId]);

    const renderData = () => {
        return (
            <Box sx={{ display: 'flex', height: '100%' }}>
                <Helmet>
                    <title>{t('easyBoardOnline')}</title>
                </Helmet>
                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            padding: '20px'
                        },
                        padding: '0px',
                        borderLeft: '1px solid'+theme.palette.storeDrawer.borderLeft.color
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                    PaperProps={{ sx: { position: "relative", padding: '0px', backgroundColor: theme.palette.dashboardLeftSidebar.background }}}
                >
                    <DrawerHeader>
                        <button
                            type="button"
                            onClick={() => { navigate('../project'); }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'none',
                                padding: '0px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <BackArrowIcon />
                            <span style={{ color: '#638CA3' }}>{t('Back')}</span>
                        </button>
                        {project ? <Typography align='center'>{project?.project?.name}</Typography> : <CircularProgress size={24} />}
                        <button
                            type="button"
                            onClick={() => { store.dispatch(setSideBarIsOpened(open ? false : true)); }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'none',
                                padding: '0px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <BackIcon />
                        </button>
                    </DrawerHeader>
                    <ProjectSidePanel />
                </Drawer>
                <Main open={open} drawerWidth={drawerWidth}>
                    <Outlet />
                </Main>
            </Box>
        )
    }

    return (
        !loading ? renderData() : <LoadingScreen />
    );
}

export default ProjectContainer;
